import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import {
  FieldValues,
  SubmitHandler,
  useForm,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { SearchCityMobileStepper } from './mobile/SearchCityMobileStepper'
import { SearchCityDatesAdultsModeEnum } from './search-city-dates-adults.enum'
import {
  getSearchCityDatesAdultClassNameByEnum,
  setDefaultValues,
  shouldRefreshBudgetValue,
  validateBudget,
} from './search-city-dates-adults.utils'
import {
  CurrentFieldName,
  Inputs,
  useFieldNavigator,
} from './useFieldNavigator'
import { useFormatSlackNotificationInput } from './useFormatSlackNotificationInput'
import { Button } from '../../atoms'
import { useSearchSessionContext } from '../../contexts'
import { AdultsPicker, DatePicker } from '../../forms'
import { AdultsPickerModeEnum } from '../../forms/adults-picker/adults-picker.enum'
import { BudgetPicker } from '../../forms/budget-picker/BudgetPicker'
import { DatePickerModeEnum } from '../../forms/date-picker/date-picker.enum'
import { LocationPicker } from '../../organisms'
import { IDatesCalendarProps } from '../../organisms/dates-calendar/dates-calendar'
import { LocationPickerModeEnum } from '../../organisms/location-picker/location-picker.enum'
import { MostBookedRegionType } from '../../organisms/location-picker/location-picker.type'

interface ISearchCityDatesAdultsProps<T extends FieldValues> {
  getValues: UseFormGetValues<T>
  watch?: UseFormWatch<T>
  onSubmit: (data: T) => void
  mode?: SearchCityDatesAdultsModeEnum
  isLoading: boolean
  isHeader?: boolean
  mostBookedRegions: MostBookedRegionType[]
  isSmallDevice: boolean
  isFromSearchPage: boolean
}

export const SearchCityDatesAdultsV2 = <T extends FieldValues>({
  getValues: externalGetValues,
  watch,
  onSubmit,
  mode = SearchCityDatesAdultsModeEnum.DEFAULT,
  isLoading,
  isHeader = false,
  mostBookedRegions,
  isSmallDevice,
  isFromSearchPage,
}: ISearchCityDatesAdultsProps<T>) => {
  const { onFormatSlackInput } = useFormatSlackNotificationInput()
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch: currentWatch,
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: setDefaultValues(externalGetValues()),
  })
  const [activeStartDate, setActiveStartDate] = useState<Date>(new Date())
  const [isOpenMobileModal, setIsOpenMobileModal] = useState<boolean>(false)
  const formValues = currentWatch()
  const fields: Array<keyof Inputs> = !isHeader
    ? ['location', 'dates', 'adults', 'budget']
    : ['location']

  const {
    isFieldActive,
    openNextField,
    onReset,
    formIsValid,
    currentField,
    onChangeCurrentField,
  } = useFieldNavigator(fields)
  const { sendSlackNotification, isLoading: sendSlackNotificationLoading } =
    useSearchSessionContext()

  const onCloseMobileModal = () => {
    setIsOpenMobileModal(false)
  }

  const onOpenMobileModal = (field: CurrentFieldName) => {
    onChangeCurrentField(field)
    setIsOpenMobileModal(true)
  }

  const handleOnSubmit: SubmitHandler<Inputs> = async (data) => {
    const isValid = formIsValid(data)

    if (!isValid) {
      if (isSmallDevice) {
        setIsOpenMobileModal(true)
      }
      openNextField(
        data,
        undefined,
        currentField !== 'default' ? currentField : undefined,
      )
      return
    }

    await sendSlackNotification(onFormatSlackInput(data))

    onSubmit(data as T)
  }

  const handleFieldValid = (currentField: keyof Inputs) => () => {
    handleSubmit((data) =>
      openNextField(data, onCloseMobileModal, currentField),
    )()
  }

  const commonProps = {
    control,
    rules: { required: false },
    isMobile: isSmallDevice,
    isFullScreen: isSmallDevice,
    alternativeClassName: 'alternativeAi',
    revampPicker: true,
    onClose: onReset,
  }

  const mixpanelDataSubmitButton = {
    name: 'Submit New Search',
    data: { ...getValues(), isSmallDevice },
  }
  const dateCalendarProps: IDatesCalendarProps = {
    activeStartDate,
    setActiveStartDate,
    houseAvailabilities: [],
  }
  const getAndSetter = {
    setValue,
    getValues,
  }
  const loading = isLoading || sendSlackNotificationLoading

  useEffect(() => {
    if (!watch) {
      return
    }

    const { unsubscribe } = watch((formValues) => {
      const currentBudget = getValues('budget')
      const newBudget = formValues?.budget
        ? {
            budgetMax: formValues.budget.budgetMax,
            budgetMin: formValues.budget.budgetMin,
          }
        : undefined

      if (shouldRefreshBudgetValue(currentBudget, newBudget)) {
        const budget = validateBudget(
          formValues?.travelType,
          formValues?.budget,
        )
        setValue('budget', budget)
      }
    })

    return () => unsubscribe()
  }, [watch])

  if (isSmallDevice) {
    return (
      <SearchCityMobileStepper
        control={control}
        isLoading={loading}
        dataMixpanel={mixpanelDataSubmitButton}
        onSubmit={handleSubmit(handleOnSubmit)}
        isOpen={isOpenMobileModal}
        onCloseModal={onCloseMobileModal}
        onOpenMobileModal={onOpenMobileModal}
        currentField={currentField}
        formValues={formValues}
        mostBookedRegions={mostBookedRegions}
        dateCalendarProps={dateCalendarProps}
        onValid={handleFieldValid}
        getAndSetter={getAndSetter}
        isFromSearchPage={isFromSearchPage}
      />
    )
  }

  return (
    <div
      className={clsx(s[getSearchCityDatesAdultClassNameByEnum(mode)], {
        [s.houseSearchHeader]: isHeader,
      })}
    >
      <div className={s.placePickerContainer}>
        <LocationPicker
          name='location'
          className={s.placePicker}
          locationTypes={[
            'locality',
            'administrative_area_level_1',
            'administrative_area_level_2',
            'colloquial_area',
            'natural_feature',
          ]}
          mode={LocationPickerModeEnum.HOUSE_SEARCH}
          mostBookedRegions={mostBookedRegions}
          isOpen={isFieldActive('location')}
          onValid={handleFieldValid('location')}
          {...commonProps}
        />
      </div>
      {!isHeader && (
        <>
          <div className={clsx(s.datePickerContainer, s.datePickerRevamp)}>
            <DatePicker
              hideLabels
              name='dates'
              getAndSetter={getAndSetter}
              className={s.datesPicker}
              dateCalendarProps={dateCalendarProps}
              mode={DatePickerModeEnum.HOUSE_SEARCH}
              innerModalClassName={s.innerModal}
              modalClassName={s.datePickerModal}
              isOpen={isFieldActive('dates')}
              onValid={handleFieldValid('dates')}
              {...commonProps}
            />
          </div>
          <div className={s.adultsPickerContainer}>
            <AdultsPicker
              name='adults'
              className={s.adultsPicker}
              mode={AdultsPickerModeEnum.HOUSE_SEARCH}
              modalClassName={s.adultsPickerModal}
              isOpen={isFieldActive('adults')}
              onValid={handleFieldValid('adults')}
              {...commonProps}
            />
          </div>
          <div className={s.budgetPickerContainer}>
            <BudgetPicker
              name='budget'
              inputContainerClassName={s.budgetPicker}
              modalClassName={s.budgetPickerModal}
              innerBudgetPickerModal={s.innerBudgetPickerModal}
              isOpen={isFieldActive('budget')}
              isFromSearchPage={isFromSearchPage}
              onValid={handleFieldValid('budget')}
              {...commonProps}
            />
          </div>
        </>
      )}
      <div>
        <Button
          className={s.searchButton}
          loading={loading}
          disabled={loading === true}
          dataMixpanel={mixpanelDataSubmitButton}
          data-testid='search-button'
          onClick={handleSubmit(handleOnSubmit)}
        >
          <FormattedMessage
            id='ui.SearchCityDatesAdults.search'
            defaultMessage='Rechercher'
          />
        </Button>
      </div>
    </div>
  )
}
